import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import ipponApi from "../config/axios";
import {
  toApiErrorPayload,
  type ApiErrorPayload,
} from "@/utils/apiErrorPayload";

const BaseURL = import.meta.env.VITE_API_URL;
const expenseListRequests = new Map<string, Promise<ExpenseRecord[]>>();

export type ExpenseSource =
  | "direct"
  | "repair"
  | "loan"
  | "recurring"
  | "budget_cell";
export type ExpenseTargetType =
  | "private"
  | "common"
  | "facility"
  | "land"
  | "other"
  | "building";

export interface ExpenseDocument {
  document_id: string;
  file_name: string;
  file_type: string;
  /** 保存済みS3キー。登録/更新payloadでは data URL(base64) も送信され、BEでS3キーへ差し替えられる。 */
  s3_key: string;
  uploaded_at: string;
}

/** 支出ステータス (BE: ExpenseStatus と同一) */
export type ExpenseStatus =
  | "下書き"
  | "AI作成"
  | "支払予定"
  | "未払い"
  | "支払済"
  | "要確認"
  | "保留"
  | "その他";

/** 内訳行の税区分 (収入と同じ 非課税 / 課税10%)。未指定は課税扱い。 */
export type ExpenseBreakdownTaxCategory = "taxable" | "non_taxable";

/** 請求書の内訳 1 行 */
export interface ExpenseBreakdownItem {
  item_id: string;
  item: string;
  /** 税抜金額 (円) */
  amount: number;
  /** 税区分。未指定は課税(taxable)。消費税 = Σ(課税行 × 税率)。 */
  tax_category?: ExpenseBreakdownTaxCategory;
}

/** グローバル ExpenseCustomField 定義に対する値 */
export interface ExpenseCustomFieldValue {
  field_id: string;
  value: string | number | boolean | string[] | null;
}

export interface ExpenseRecord {
  expense_id: string;
  client_id: string;
  property_id: string;
  name: string;
  category: string;
  expense_type: "CAPEX" | "OPEX";
  amount: number;
  /** 1 支出あたりの予算 (任意)。未設定/明示クリア時は null/undefined */
  budget?: number | null;
  date: string;
  status: ExpenseStatus;
  description?: string;

  target_type?: ExpenseTargetType;
  /** 紐付け Unit の id (BE: target_unit_id) */
  target_unit_id?: string;
  /** 登録時点の Unit 名 (snapshot) */
  target_name_snapshot?: string;

  source?: ExpenseSource;
  linked_repair_id?: string;
  linked_estimate_id?: string;
  linked_loan_id?: string;
  linked_recurring_id?: string;

  budget_category_id?: string;

  // 帳簿要件
  payee?: string;
  partner_name?: string;
  tax_rate?: number;
  tax_amount?: number;
  registration_number?: string;
  breakdown?: ExpenseBreakdownItem[];

  // カスタムフォーム
  custom_fields?: ExpenseCustomFieldValue[];

  // OCR メタ
  ocr_source_document_id?: string;
  ocr_extracted_at?: string;
  ai_filled_fields?: string[];
  ocr_confidence?: Record<string, number>;

  documents?: ExpenseDocument[];
  /** AI(OCR) 抽出元書類。詳細画面では read-only セクションで表示 */
  ai_documents?: ExpenseDocument[];

  created_at: string;
  updated_at?: string;
}

interface ExpenseState {
  list: {
    data: ExpenseRecord[];
    loading: boolean;
    error: string | null;
    loaded: boolean;
  };
  creating: boolean;
  updating: boolean;
}

const initialState: ExpenseState = {
  list: { data: [], loading: false, error: null, loaded: false },
  creating: false,
  updating: false,
};

export const fetchExpenses = createAsyncThunk<
  ExpenseRecord[],
  { property_id: string; fiscal_year?: number }
>(
  "expenses/fetchExpenses",
  async ({ property_id, fiscal_year }, { rejectWithValue }) => {
    const requestKey = `${property_id}:${fiscal_year ?? "all"}`;
    try {
      let request = expenseListRequests.get(requestKey);
      if (!request) {
        const params: Record<string, string | number> = { property_id };
        if (fiscal_year !== undefined) params.fiscal_year = fiscal_year;
        request = ipponApi
          .get(`${BaseURL}/api/v1/expenses`, { params })
          .then((res) => res.data.data.items || []);
        expenseListRequests.set(requestKey, request);
        void request
          .finally(() => {
            if (expenseListRequests.get(requestKey) === request) {
              expenseListRequests.delete(requestKey);
            }
          })
          .catch(() => {});
      }
      return await request;
    } catch (err: unknown) {
      return rejectWithValue(
        toApiErrorPayload(err, "支出データの取得に失敗しました"),
      );
    }
  },
);

export const fetchExpenseById = createAsyncThunk<
  ExpenseRecord,
  { expense_id: string },
  { rejectValue: ApiErrorPayload }
>("expenses/fetchById", async ({ expense_id }, { rejectWithValue }) => {
  try {
    const res = await ipponApi.get(`${BaseURL}/api/v1/expenses/${expense_id}`);
    return res.data.data;
  } catch (error: unknown) {
    return rejectWithValue(
      toApiErrorPayload(error, "Failed to fetch expense detail"),
    );
  }
});

export const createExpense = createAsyncThunk<
  ExpenseRecord,
  {
    property_id: string;
    name: string;
    category: string;
    expense_type: "CAPEX" | "OPEX";
    amount: number;
    /** 1 支出あたりの予算 (任意) */
    budget?: number;
    date: string;
    status: ExpenseStatus;
    description?: string;
    target_type?: ExpenseTargetType;
    target_unit_id?: string;
    target_name_snapshot?: string;
    source: ExpenseSource;
    linked_repair_id?: string;
    linked_estimate_id?: string;
    budget_category_id?: string;
    payee?: string;
    partner_name?: string;
    tax_rate?: number;
    tax_amount?: number;
    registration_number?: string;
    breakdown?: ExpenseBreakdownItem[];
    custom_fields?: ExpenseCustomFieldValue[];
    ocr_source_document_id?: string;
    ocr_extracted_at?: string;
    ai_filled_fields?: string[];
    ocr_confidence?: Record<string, number>;
    documents?: ExpenseDocument[];
    ai_documents?: ExpenseDocument[];
    // 旧互換
    area_type?: string;
    area_name?: string;
  }
>("expenses/createExpense", async (body, { rejectWithValue }) => {
  try {
    const res = await ipponApi.post(`${BaseURL}/api/v1/expenses`, body);
    return res.data.data;
  } catch (err: unknown) {
    return rejectWithValue("支出の登録に失敗しました");
  }
});

export const updateExpense = createAsyncThunk<
  ExpenseRecord,
  { expense_id: string; body: Partial<ExpenseRecord> }
>(
  "expenses/updateExpense",
  async ({ expense_id, body }, { rejectWithValue }) => {
    try {
      const res = await ipponApi.put(
        `${BaseURL}/api/v1/expenses/${expense_id}`,
        body,
      );
      return res.data.data;
    } catch (err: unknown) {
      return rejectWithValue("支出の更新に失敗しました");
    }
  },
);

export const deleteExpense = createAsyncThunk<string, { expense_id: string }>(
  "expenses/deleteExpense",
  async ({ expense_id }, { rejectWithValue }) => {
    try {
      await ipponApi.delete(`${BaseURL}/api/v1/expenses/${expense_id}`);
      return expense_id;
    } catch (err: unknown) {
      return rejectWithValue("支出の削除に失敗しました");
    }
  },
);

// ── 支出 OCR (請求書 / 領収書から自動抽出) ──

export interface ExpenseOcrExtracted {
  name: string | null;
  budget_category_id: string | null;
  category_raw: string | null;
  expense_type: "OPEX" | "CAPEX" | null;
  amount: number | null;
  payee: string | null;
  partner_name: string | null;
  tax_rate: number | null;
  tax_amount: number | null;
  registration_number: string | null;
  breakdown: { item: string | null; amount: number | null }[];
  notes: string | null;
}

export interface ExpenseOcrResponse {
  document: {
    document_id: string;
    s3_key: string;
    /** FE プレビュー用の署名付き URL (BE 発行) */
    signed_url: string;
    file_name: string;
    file_type: string;
  };
  extracted: ExpenseOcrExtracted;
  warnings: string[];
}

export const extractExpenseOcr = createAsyncThunk<
  ExpenseOcrResponse,
  { property_id: string; file: File }
>("expenses/extractOcr", async ({ property_id, file }, { rejectWithValue }) => {
  try {
    const formData = new FormData();
    formData.append("property_id", property_id);
    // attachFieldsToBody: "keyValues" ではファイルが Buffer になり filename
    // メタデータが失われるため、別フィールドで明示的に渡す (chatSlice 同パターン)
    formData.append("file_name", file.name);
    formData.append("file_type", file.type || "application/pdf");
    formData.append("file", file);
    const res = await ipponApi.post(
      `${BaseURL}/api/v1/expenses/ocr/extract`,
      formData,
      { headers: { "Content-Type": "multipart/form-data" } },
    );
    return res.data.data as ExpenseOcrResponse;
  } catch (err: any) {
    return rejectWithValue(
      err?.response?.data?.message || "OCR 抽出に失敗しました",
    );
  }
});

const expenseSlice = createSlice({
  name: "expenses",
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(fetchExpenses.pending, (state) => {
        state.list.loading = true;
        state.list.error = null;
      })
      .addCase(fetchExpenses.fulfilled, (state, action) => {
        state.list.loading = false;
        state.list.data = action.payload;
        state.list.loaded = true;
      })
      .addCase(fetchExpenses.rejected, (state, action) => {
        state.list.loading = false;
        state.list.error = action.payload as string;
      })
      .addCase(createExpense.pending, (state) => {
        state.creating = true;
      })
      .addCase(createExpense.fulfilled, (state, action) => {
        state.creating = false;
        state.list.data.unshift(action.payload);
      })
      .addCase(createExpense.rejected, (state) => {
        state.creating = false;
      })
      .addCase(updateExpense.pending, (state) => {
        state.updating = true;
      })
      .addCase(updateExpense.fulfilled, (state, action) => {
        state.updating = false;
        const idx = state.list.data.findIndex(
          (e) => e.expense_id === action.payload.expense_id,
        );
        if (idx >= 0) state.list.data[idx] = action.payload;
      })
      .addCase(updateExpense.rejected, (state) => {
        state.updating = false;
      })
      .addCase(deleteExpense.fulfilled, (state, action) => {
        state.list.data = state.list.data.filter(
          (e) => e.expense_id !== action.payload,
        );
      });
  },
});

export default expenseSlice.reducer;
